import { ThemeConfig, EXPREALTY_CA_THEME_NAME, ThemeColors } from '@tenants/types';
import { translate } from 'utils/translations';
import { SiteLanguage } from 'types/locales';
import keywordsCA from '@tenants/definitions/shared/keywords-CA';

//#region Public Constants
export const ExpCaThemeColors: ThemeColors = {
  primaryVeryLight: '#8ECFFF',
  primaryLight:'#E8EDF5',
  primary: '#0C0F24',
  primaryDark: '#0C0F24',
  primaryVeryDark: '#0C0F24',
  primaryBackground: '#FFF',
  primaryForSale: '#FFF',
  primarySold: '#6D071A',
  primarySoldFocus: '#6D071A',
  primaryPending: '#D94A3A',
  primaryPendingFocus: '#D94A3A',
  primaryFilters: '#0C0F24',
  primaryFontColor: '#0C0F24',
  primaryLinkColor: '#1B489B',
  primaryFocusLinkColor: '#1B489B',
  primaryInvertedBackground: '#FFFFFF',
  primaryBorderColor: '#5A5A5A',
  primaryFocusColor: '#F3F4F4',
  primaryFocusBorderColor: '#0C0F24',
  primaryChart: '#F3F3F4',
  secondaryBackground: '#FFF',
  secondaryBorderColor: '#5A5A5A',
  secondaryLinkColor: '#3D3F50',
  secondaryHoverColor: '#0C0F24',
  secondaryFocusColor: '#3D3F50',
  secondaryFocusBorderColor: '#5A5A5A',
  tertiaryBackground: '#EFEFEF',
  tertiaryBackgroundLight: '#3D3F50',
  tertiaryBorderColor: '#5A5A5A',
  tertiaryLinkColor: '#1B489B',
  quaternaryLinkColor: '#496DAF',
  quaternaryBorderColor: '#5A5A5A',
  quaternaryBackgroundLight: '#FFFFFF',
  transparentBackground: 'rgba(255, 255, 255, 0.1)',
  mapPinForSale: '#1B489B',
  mapPinForSaleFocus: '#1B489B',
  mapControlBackground: '#FFF',
  mapControlBackgroundHover: '#F3F4F4',
  mapControlBackgroundActive: '#E7E7E9',
  carouselArrowHover: '#F3F4F4',
  carouselArrowFocus: '#CECFD3',
  notFound404PinHeadColor: '#1B489B',
  notFound404PinBodyColor: '#496DAF',
};
//#endregion

//#region Theme Config Definition
const ExprealtyCaTheme: ThemeConfig<typeof EXPREALTY_CA_THEME_NAME> = {
  id: EXPREALTY_CA_THEME_NAME,
  name: 'eXp Realty',
  title: 'eXp Realty - eXp Realty® Canada',
  faviconPath: '/favicon-exp.ico',
  metaDescription: 'Largest residential real estate brokerage in North America. Where the pros go to grow.',
  largeDesktopHeaderImage: '/next/assets/images/header-image-large-desktop-exp-ca.jpg',
  desktopHeaderImage: '/next/assets/images/header-image-desktop-exp-ca.jpg',
  mobileHeaderImage: '/next/assets/images/header-image-mobile-exp-ca.jpg',
  smallMobileImage: '/next/assets/images/header-image-small-mobile-exp-ca.jpg',
  ogSiteName: 'eXp Realty® Canada',
  ogImage: 'https://exprealty.ca/assets/images/header-image2.jpg',
  schemaUrl: 'https://www.exprealty.ca',
  schemaLogo: 'https://exprealty.ca/next/assets/images/eXp-Realty-logo.png',
  schemaLocation: {
    addressCountry: 'Canada',
    addressLocality: 'Victoria',
    addressRegion: 'BC',
    postalCode: 'V8W2C4',
    streetAddress: '800 - 1070 Douglas St.',
  },
  schemaFoundingDate: '2002',
  schemaFounders: [
    {
      '@type': 'Person',
      name: 'Sanford Glenn Darrel',
    },
    {
      '@type': 'Person',
      name: 'Brian Culhane',
    },
  ],
  colors: ExpCaThemeColors,
  fonts: {
    heading: 'Manrope',
    text: 'Manrope',
    headingWeight: '300',
    headingBoldWeight: '600',
  },
  decorations: {
    brushstrokes: false,
    boxShadow: '0px 2px 4px 0px #00000014',
    boxShadowLight: '0 0 8px 0 rgba(0, 0, 0, .1)',
    areaPageLineWidth: '51px',
  },
  buttons: {
    borderRadius: '100px',
    borderRadiusSmall: '8px',
    borderRadiusMedium: '8px',
    borderRadiusLarge: '100px',
    borderRadiusModal: '12px',
    borderRadiusBanner: '12px',
  },
  icons: {
    borderRadius: '100px',
  },
  style_overrides: {
    breadcrumbs: {
      mobileMarginTop: '1.5em',
      boxShadow: '0px 2px 4px 0px #00000014;',
    },
    areaListingsPage: {
      pageTitleFontWeight: 'bold',
    },
    locationUnavailable: {
      imagePath: '/next/assets/images/listing-page/location-unavailable-exp.svg',
    },
    icon: {
      checkmarkCircleIcon: {
        fill: ExpCaThemeColors.primaryLinkColor,
      },
    },
  },
  searchOptions: [
    {
      type: 'Buy',
      placeholder(siteLocation, isOnMobile) {
        const neighbourhood = translate('neighbourhood', siteLocation);
        return isOnMobile ? `City, ${neighbourhood}, or address` : `Enter a city, ${neighbourhood}, address, MLS® number or school`;
      },
    },
    {
      type: 'Rent',
      placeholder(siteLocation, isOnMobile) {
        const neighbourhood = translate('neighbourhood', siteLocation);
        return isOnMobile ? `City, ${neighbourhood}, or address` : `Enter a city, ${neighbourhood}, address, MLS® number or school`;
      },
    },
    {
      type: 'Sold',
      placeholder(siteLocation, isOnMobile) {
        const neighbourhood = translate('neighbourhood', siteLocation);
        return isOnMobile ? `City, ${neighbourhood}, or address` : `Enter a city, ${neighbourhood}, address, MLS® number or school`;
      },
    },
    {
      type: 'Agents',
      placeholder(siteLocation) {
        const neighbourhood = translate('neighbourhood', siteLocation);
        return `Enter a city or ${neighbourhood}`;
      },
    },
    {
      type: 'HomeAppraisal',
      placeholder() {
        return 'Enter your home address';
      },
    },
  ],
  contentBlockBuy: {
    title: 'Buying your next home',
    copy: 'eXp Realty transforms home buying into a seamless experience, tailored to your needs with cutting-edge technology and expert support. Discover a better way to buy a home with eXp.',
    image: '/next/assets/images/content-block/exp-buy.jpg',
    link: '/buy',
  },
  contentBlockSell: {
    title: 'Selling your home',
    copy: 'Discover a smarter selling experience with eXp Realty with a simplified process that blends advanced technology and market insight to sell your home effectively and efficiently.',
    image: '/next/assets/images/content-block/exp-sell.jpg',
    link: '/sell',
  },
  privacyPolicy: (language?: SiteLanguage) => {
    switch (language) {
      case SiteLanguage.French:
      case SiteLanguage.FrenchCA:
        return 'https://expworldholdings.com/privacy-policy-french/';
      default:
        return 'https://expworldholdings.com/privacy-policy/';
    }
  },
  termsOfUse: (language?: SiteLanguage) => {
    switch (language) {
      case SiteLanguage.French:
      case SiteLanguage.FrenchCA:
        return 'https://expworldholdings.com/terms-of-service-french/';
      default:
        return 'https://expworldholdings.com/terms-of-service/';
    }
  },
  keywords: keywordsCA,
};
//#endregion

//#region Export
export default ExprealtyCaTheme;
